'use client';
import React, { cloneElement, isValidElement, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { IS_PROD, SITE } from '@/app/constants';
import { useCopy, useDevice } from '@/app/hooks';
import { hysdk } from '@/app/utils/client';
import { DOWNLOAD_URL, GO_VIDU_APP_HOST, GO_VIDU_APP_URL } from '../../constants';
import type { ViduV1Template } from '../../types';
import { useWechat } from '../context/WechatContext';
import { Base64Util, getQueryParams } from '../utils';

// 临时开关：分享落地页 Try Now 是否走"Vidu Pro App"专用 scheme/universal link。
// 旧逻辑（站内跳 /claw）仍保留；下线时把开关翻 false 即可一键回滚，确认稳定后整体移除。
export const USE_NEW_PRO_APP_SCHEME = true;

// Vidu Pro App universal link / scheme 跳转的域名
export const PRO_APP_HOST = SITE === 'china' ? 'go.vidu.cn' : 'go.vidu.com';

// Vidu Pro App Android custom scheme 协议头
export const PRO_APP_ANDROID_SCHEME = SITE === 'china' ? 'viduaipro' : 'vidupro';

interface UseDownloadOptions {
  renderCallAppContent?: () => ReactNode;
  template?: ViduV1Template;
  wechatLaunchWrapperClassName?: string;
  /**
   * 新版 Pro App URL 后追加的路径/query，例如 'tab?target=claw'。
   * 仅 USE_NEW_PRO_APP_SCHEME=true 时生效；不传则裸 URL。
   */
  proAppTarget?: string;
  /**
   * 桌面端（非 iOS / Android）点击时的回退行为。
   * 不传则桌面端行为与原 handleH5Download 一致（直接对浏览器跳 universal link）。
   */
  desktopFallback?: () => void;
  /**
   * 拉端按钮被点击时的回调（用于埋点）。
   * 会在两条渲染分支都触发：
   * - 普通/克隆分支：按钮 onClick 时；
   * - 微信安卓开放标签分支：透明覆盖层拦截了底层按钮 onClick，
   *   改为监听包装容器的原生 click（物理点击一定冒泡到容器，每次恰好一次）。
   */
  onCallAppClick?: () => void;
}

// 微信开放标签包装组件 - 直接渲染传入的children
const WechatLaunchWrapper = ({
  children,
  extinfo,
  onError,
  onLaunchClick,
  className,
}: {
  children?: ReactNode;
  extinfo: string;
  onError: () => void;
  onLaunchClick?: () => void;
  className?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useRef(`launch-btn-${Math.random().toString(36).substr(2, 9)}`);

  // 用 ref 读取最新回调，避免把 onLaunchClick 放进 effect 依赖导致开放标签被反复重建
  const onLaunchClickRef = useRef(onLaunchClick);
  onLaunchClickRef.current = onLaunchClick;

  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;

      // 点击埋点：wx-open-launch-app 覆盖层拦截了底层按钮 onClick，
      // 但用户的物理点击一定会冒泡到容器 div，这里监听最可靠、且每次点击恰好一次
      // （不依赖 launch/error 事件——后者在部分微信版本不稳定，且可能漏报或重复）。
      const handleContainerClick = () => {
        onLaunchClickRef.current?.();
      };
      container.addEventListener('click', handleContainerClick);
      const wechatHTML = `
        <wx-open-launch-app
          id="${uniqueId.current}"
          appid="wx734e069c383e93a5"
          extinfo="${extinfo}"
          style="position: absolute; top: 0; left: 0; display: block; width: 100%; height: 100%; z-index: 2; opacity: 0; background: transparent; border: 0; overflow: hidden;"
        >
          <script type="text/wxtag-template">
            <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; background: transparent;"></div>
          </script>
        </wx-open-launch-app>
      `;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = wechatHTML;
      const wechatElement = tempDiv.firstElementChild;

      if (wechatElement) {
        container.appendChild(wechatElement);
      }

      setTimeout(() => {
        const launchBtn = container.querySelector(`#${uniqueId.current}`);
        launchBtn?.addEventListener('error', (e) => {
          const ce = e as CustomEvent;
          console.error('[WechatLaunch] wx-open-launch-app 拉起失败，回退到应用商店', {
            extinfo,
            type: ce.type,
            detail: ce.detail,
            detailJSON: JSON.stringify(ce.detail),
          });
          onError();
        });
      }, 100);

      return () => {
        container.removeEventListener('click', handleContainerClick);
        const wechatElement = container.querySelector(`#${uniqueId.current}`);
        if (wechatElement) {
          wechatElement.remove();
        }
      };
    }
  }, [extinfo, onError]);

  // 直接渲染传入的children，不创建额外的包装
  return React.createElement(
    'div',
    {
      ref: containerRef,
      className,
      style: { position: 'relative' },
    },
    children,
  );
};

export const useDownload = ({
  renderCallAppContent,
  template,
  wechatLaunchWrapperClassName,
  proAppTarget,
  desktopFallback,
  onCallAppClick,
}: UseDownloadOptions = {}) => {
  const { wxSDKReady, isWechat } = useWechat();
  const { copy } = useCopy();

  // 用 ref 持有最新回调，保证传给 WechatLaunchWrapper 的函数引用稳定，
  // 避免因内联回调每次 render 变化导致微信开放标签被反复重建。
  const onCallAppClickRef = useRef(onCallAppClick);
  onCallAppClickRef.current = onCallAppClick;
  const stableCallAppClick = useCallback(() => {
    onCallAppClickRef.current?.();
  }, []);

  const { os, isMobile } = useDevice();
  const isAndroid = os === 'android';

  const redirectToStore = useCallback(() => {
    window.location.href = DOWNLOAD_URL;
  }, []);

  const tryOpenApp = useCallback(
    (url: string) => {
      // 使用隐藏的a标签模拟点击
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // 只处理安卓设备：1.5秒后如果页面还可见，说明app没有被拉起，跳转到下载页面
      if (isAndroid) {
        let hasNavigated = false;

        const timeoutId = setTimeout(() => {
          if (!hasNavigated && !document.hidden) {
            hasNavigated = true;
            redirectToStore();
          }
        }, 1500);

        const handleVisibilityChange = () => {
          if (document.hidden && !hasNavigated) {
            clearTimeout(timeoutId);
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        setTimeout(() => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }, 2000);
      }
    },
    [isAndroid, redirectToStore],
  );

  /**
   * 生成webview打开的页面链接
   * @returns
   */
  const generateOriginalUrl = useCallback(() => {
    // 新版 Pro App：裸 URL（仅追加调用方指定的 target 后缀），不带 H5 投放 query。
    // iOS 走 universal link，Android 同时给出 https + custom scheme 双保险。
    if (USE_NEW_PRO_APP_SCHEME) {
      const targetSuffix = proAppTarget ?? '';
      const httpsBase = `https://${PRO_APP_HOST}/pro-app/${targetSuffix}`;
      if (isAndroid) {
        return [httpsBase, `${PRO_APP_ANDROID_SCHEME}://${PRO_APP_HOST}/${targetSuffix}`];
      }
      return [httpsBase];
    }

    // iOS 全部用 https 安卓除了 https 还需要用 schema 协议
    const httpsUrl = 'https';
    let schemaUrl = 'https';
    if (isAndroid) {
      schemaUrl = SITE === 'china' ? 'viduai' : 'vidu';
    }
    const baseUrl = IS_PROD ? `${httpsUrl}://${GO_VIDU_APP_HOST}` : GO_VIDU_APP_URL;
    const schemaBaseUrl = IS_PROD ? `${schemaUrl}://${GO_VIDU_APP_HOST}` : GO_VIDU_APP_URL;

    const params = getQueryParams(window.location.href);

    const { biz_code } = params;
    if (biz_code === 'activity') {
      params.object_type = 'activity';
    }

    if (template) {
      params.object_id = template.id;
      params.utm_content = template.id;
      params.object_scene = template.scene;
    }

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    const url = window.location.href;
    const finalQueryString = `${queryString}&url=${encodeURIComponent(url)}`;

    const urls = [biz_code === 'creation' ? baseUrl : `${baseUrl}?${finalQueryString}`];

    // 安卓设备添加 schema 协议的 URL
    if (isAndroid) {
      urls.push(biz_code === 'creation' ? schemaBaseUrl : `${schemaBaseUrl}?${finalQueryString}`);
    }

    return urls;
  }, [isAndroid, template, proAppTarget]);

  // H5 deeplink唤起下载
  const handleH5Download = useCallback(() => {
    const urls = generateOriginalUrl();
    urls.forEach((url, index) => {
      setTimeout(() => {
        const base64EncodedUrl = Base64Util.encode(url);
        tryOpenApp(url);
        copy(base64EncodedUrl, { hideMessage: true });
      }, index * 100);
    });
  }, [generateOriginalUrl, tryOpenApp, copy]);

  /**
   * 业务统一入口：调用方在容器/按钮 onClick 中直接调它。
   * - 桌面 + 提供 desktopFallback：执行 fallback（站内导航）
   * - 其他情况：触发 deeplink 拉起
   * 注：微信安卓的真正拉起由 callAppContent 包装的 wx-open-launch-app 接管，本函数只是兜底。
   */
  const triggerCallApp = useCallback(() => {
    if (!isMobile && desktopFallback) {
      desktopFallback();
      return;
    }
    handleH5Download();
  }, [isMobile, desktopFallback, handleH5Download]);

  // App内视频下载
  const handleVideoDownload = (videoUri: string) => {
    if (hysdk.sniff.isViduApp) {
      // App内用户直接下载视频
      hysdk.download(videoUri);
    } else {
      handleH5Download();
    }
  };

  const shouldUseWechatLaunch = isWechat && isAndroid && wxSDKReady;
  const callAppContent = useMemo(() => {
    if (!renderCallAppContent) return null;

    const ButtonComponent = renderCallAppContent();

    // 微信安卓环境，使用微信开放标签包装组件。extinfo 取首项（https universal link）。
    if (shouldUseWechatLaunch) {
      const originalUrls = generateOriginalUrl();
      return React.createElement(
        WechatLaunchWrapper,
        {
          extinfo: originalUrls[0],
          onError: redirectToStore,
          // 覆盖层拦截了底层按钮点击，改由包装容器的原生 click 补报点击埋点
          onLaunchClick: stableCallAppClick,
          className: wechatLaunchWrapperClassName,
        },
        ButtonComponent,
      );
    }

    // 非微信安卓环境，克隆原始元素并添加点击事件
    if (isValidElement(ButtonComponent)) {
      const buttonElement = ButtonComponent as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
      return cloneElement(buttonElement, {
        ...(buttonElement.props || {}),
        onClick: (e: React.MouseEvent) => {
          // 先执行原有的点击事件（如果有的话）
          if (buttonElement.props.onClick) {
            buttonElement.props.onClick(e);
          }
          // 点击埋点：与微信分支的 onLaunchClick 对齐，保证两条分支都上报
          stableCallAppClick();
          // 然后执行下载/拉起逻辑（桌面端走 desktopFallback）
          triggerCallApp();
        },
      });
    }

    return ButtonComponent;
  }, [
    renderCallAppContent,
    shouldUseWechatLaunch,
    generateOriginalUrl,
    triggerCallApp,
    redirectToStore,
    stableCallAppClick,
    wechatLaunchWrapperClassName,
  ]);

  return {
    generateOriginalUrl,
    handleH5Download,
    handleVideoDownload,
    redirectToStore,
    triggerCallApp,
    callAppContent,
  };
};
